import React, { useEffect, useRef, useState } from 'react';
import { ArrowDown, ArrowUpRight, CheckCircle } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { AnimatedText } from '../components/AnimatedText';
import { useBookingModal } from '../contexts/BookingModalContext';



export const OutpatientPage: React.FC = () => {
    const heroRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const mainRef = useRef<HTMLDivElement>(null);
    const { openModal } = useBookingModal();

    useEffect(() => {


        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (videoRef.current && prefersReducedMotion) {
            videoRef.current.pause();
        }

        if (prefersReducedMotion) return;

        const ctx = gsap.context(() => {
            // Hero animations (AnimatedText handles title animation)
            const heroTl = gsap.timeline({ defaults: { ease: 'power3.out' } });

            heroTl
                .from('.hero-divider', {
                    scaleX: 0,
                    duration: 1,
                    stagger: 0.2,
                    delay: 1.2,
                })
                .from('.hero-intro-text', {
                    opacity: 0,
                    y: 40,
                    duration: 1,
                }, '-=0.5')
                .from('.hero-scroll-indicator', {
                    opacity: 0,
                    y: -20,
                    duration: 0.6,
                }, '-=0.3');

            // Therapy section images - parallax effect
            gsap.utils.toArray('.therapy-image').forEach((img: any) => {
                gsap.to(img, {
                    yPercent: -10,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: img,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: 1,
                    },
                });
            });

            // Therapy section image containers - reveal animation
            gsap.utils.toArray('.therapy-image-container').forEach((container: any) => {
                gsap.from(container, {
                    opacity: 0,
                    x: container.classList.contains('from-right') ? 80 : -80,
                    duration: 1.2,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: container,
                        start: 'top 80%',
                        toggleActions: 'play none none none',
                    },
                });
            });

            // Therapy content cards - slide up with fade
            gsap.utils.toArray('.therapy-card').forEach((card: any) => {
                gsap.from(card, {
                    opacity: 0,
                    y: 60,
                    duration: 1,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: card,
                        start: 'top 85%',
                        toggleActions: 'play none none none',
                    },
                });
            });

            // Section headlines animation
            gsap.from('.section-headline', {
                opacity: 0,
                y: 40,
                duration: 1,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: '.section-headline',
                    start: 'top 85%',
                    toggleActions: 'play none none none',
                },
            });

            // Pill tags stagger animation
            gsap.set('.pill-tag', { opacity: 1 }); // Ensure visibility
            gsap.fromTo('.pill-tag',
                { opacity: 0, y: 20, scale: 0.9 },
                {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: 0.6,
                    stagger: 0.1,
                    ease: 'back.out(1.7)',
                    scrollTrigger: {
                        trigger: '.pills-container',
                        start: 'top 90%',
                        toggleActions: 'play none none none',
                    },
                }
            );

        }, mainRef);

        return () => ctx.revert();
    }, []);

    return (
        <div ref={mainRef} className="outpatient-page bg-white">
            {/* Hero Section - Matching DrugAlcoholServicesPage design */}
            <section
                ref={heroRef}
                className="relative min-h-screen overflow-hidden"
                aria-label="Outpatient Therapy Groups Hero"
            >
                {/* Video Background */}
                <video
                    ref={videoRef}
                    className="absolute inset-0 w-full h-full object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="none"
                    poster="/images/Outpatients.jpg"
                    aria-hidden="true"
                >
                    <source src="/videos/Outpatients.mp4" type="video/mp4" />
                </video>

                {/* Dark Gradient Overlay */}
                <div
                    className="absolute inset-0"
                    style={{
                        background: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.5) 100%)',
                    }}
                    aria-hidden="true"
                />

                {/* Hero Content - Full height flex layout */}
                <div className="relative z-10 w-full h-screen flex flex-col px-6 sm:px-8 lg:px-12">
                    {/* Top Section - Title Area (spans ~55-60% of width) */}
                    <div className="flex-1 flex flex-col justify-center pt-24">
                        {/* Main Title - Large, at top, spanning 55-60% */}
                        <div className="max-w-[60%]">
                            <div className="text-5xl md:text-6xl lg:text-6xl xl:text-7xl font-serif text-white leading-[0.95]">
                                <AnimatedText
                                    text="Outpatient Therapy"
                                    as="span"
                                    className="block"
                                    delay={200}
                                    letterDelay={35}
                                    duration={600}
                                />
                                <AnimatedText
                                    text="Groups"
                                    as="span"
                                    className="block"
                                    delay={700}
                                    letterDelay={35}
                                    duration={600}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Bottom Section - Two columns with divider lines */}
                    <div className="pb-12 lg:pb-16">
                        {/* Thin Divider Line - Full width */}
                        <div className="hero-divider w-full h-px bg-white/30 mb-8 origin-left"></div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
                            {/* Left Column - Empty */}
                            <div className="hidden lg:block">
                            </div>

                            {/* Right Column - Introduction */}
                            <div className="flex flex-col">
                                {/* Thin Divider Line - Right section */}
                                <div className="hero-divider w-full h-px bg-white/30 mb-6 origin-left lg:hidden"></div>

                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-4 hero-intro-text">
                                            Heal Together
                                        </p>
                                        <p
                                            className="hero-intro-text text-white/80 leading-relaxed"
                                            style={{
                                                fontSize: 'clamp(0.95rem, 1.5vw, 1.1rem)',
                                                maxWidth: '380px',
                                            }}
                                        >
                                            Experience healing and personal growth through our tailored therapy sessions.
                                        </p>
                                    </div>

                                    {/* Scroll Down Indicator - Right side */}
                                    <button
                                        onClick={() => {
                                            const nextSection = document.querySelector('.outpatient-content');
                                            if (nextSection) {
                                                nextSection.scrollIntoView({ behavior: 'smooth' });
                                            }
                                        }}
                                        className="hero-scroll-indicator flex flex-col items-center ml-8 group cursor-pointer"
                                        aria-label="Scroll to next section"
                                    >
                                        <ArrowDown className="w-5 h-5 text-[#d68800] group-hover:text-white animate-bounce transition-colors duration-300" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Individual Therapy Section */}
            <section className="outpatient-content py-20 lg:py-28" style={{ backgroundColor: '#fdf8f0' }}>
                <div className="w-full px-6 sm:px-8 lg:px-12">
                    {/* Section Headline */}
                    <h2
                        className="section-headline text-center text-3xl md:text-4xl lg:text-5xl font-serif text-mscg-dark-gray mb-12"
                        style={{ fontWeight: 500 }}
                    >
                        Navigating Life's <span className="text-gray-600">Most Difficult</span> Transitions?
                    </h2>

                    {/* Pill Tags Row */}
                    <div className="pills-container flex flex-wrap justify-center gap-3 md:gap-4 mb-16">
                        {[
                            'Supportive Space',
                            'No-Judgment',
                            'Heal Faster',
                            'Deepen Self-Awareness',
                            'Personal Growth',
                        ].map((label, index) => (
                            <div
                                key={index}
                                className="pill-tag flex items-center gap-2 px-4 py-2"
                            >
                                <CheckCircle className="w-5 h-5 text-[#d68800]" />
                                <span className="text-base font-medium text-mscg-dark-gray tracking-wide">{label}</span>
                            </div>
                        ))}
                    </div>

                    {/* Image with Overlaid Content Card */}
                    <div className="relative w-full">
                        {/* Main Image - Offset to the right to allow card overflow on left */}
                        <div className="therapy-image-container ml-auto w-full lg:w-[85%] rounded-3xl overflow-hidden shadow-2xl">
                            <img
                                src="/images/CounselingSession (1).jpeg"
                                alt="Individual therapy session with MSCG counselor"
                                className="therapy-image w-full h-[500px] md:h-[600px] object-cover"
                            />
                        </div>

                        {/* Overlaid Content Card - Positioned to overflow left of image */}
                        <div
                            className="therapy-card relative lg:absolute lg:bottom-12 lg:left-0 bg-white rounded-2xl p-8 md:p-10 shadow-xl max-w-xl mt-[-60px] mx-6 lg:mx-0 lg:mt-0"
                            style={{ backdropFilter: 'blur(10px)' }}
                        >
                            <h3 className="text-2xl md:text-3xl font-serif text-mscg-dark-gray mb-4 leading-tight">
                                Individual Therapy
                            </h3>
                            <p className="text-neutral-600 leading-relaxed mb-6 text-sm md:text-base">
                                In a safe, confidential, and supportive space, you'll have the opportunity to explore your thoughts, emotions, and challenges. Whether you're dealing with anxiety, depression, relationship struggles, or navigating life transitions, our skilled therapist will partner with you to create effective coping strategies and deepen self-awareness.
                            </p>
                            <button
                                onClick={openModal}
                                className="inline-flex items-center gap-3 bg-mscg-dark-gray text-white pl-6 pr-2 py-2.5 rounded-full font-medium text-sm transition-all duration-300 hover:bg-[#d68800] hover:scale-105 group"
                            >
                                Book Now
                                <div className="w-8 h-8 rounded-full bg-[#d68800] text-white flex items-center justify-center transition-all duration-300 group-hover:bg-white group-hover:text-[#d68800] group-hover:rotate-45">
                                    <ArrowUpRight className="w-4 h-4" />
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Couples Therapy Section */}
            <section className="pt-0 pb-20 lg:pb-28" style={{ backgroundColor: '#fdf8f0' }}>
                <div className="w-full px-6 sm:px-8 lg:px-12">
                    {/* Image with Overlaid Content Card - Reversed layout */}
                    <div className="relative w-full">
                        {/* Main Image - Offset to the left to allow card overflow on right */}
                        <div className="therapy-image-container from-right mr-auto w-full lg:w-[85%] rounded-3xl overflow-hidden shadow-2xl">
                            <video
                                src="/videos/CoupleTherapy.mp4"
                                autoPlay
                                loop
                                muted
                                playsInline
                                preload="none"
                                className="therapy-image w-full h-[500px] md:h-[600px] object-cover"
                            />
                        </div>

                        {/* Overlaid Content Card - Dark variant, positioned to overflow right of image */}
                        <div
                            className="therapy-card relative lg:absolute lg:bottom-12 lg:right-0 bg-mscg-dark-gray rounded-2xl p-8 md:p-10 shadow-xl max-w-xl mt-[-60px] mx-6 lg:mx-0 lg:mt-0"
                            style={{ backdropFilter: 'blur(10px)' }}
                        >
                            <h3 className="text-2xl md:text-3xl font-serif text-white mb-4 leading-tight">
                                Couples Therapy
                            </h3>
                            <p className="text-white/70 leading-relaxed mb-6 text-sm md:text-base">
                                Strengthen relationships and foster deeper connections through our couples and family therapy sessions. In a safe, confidential, and supportive environment, you and your loved ones can explore communication patterns, emotional dynamics, and unresolved conflicts.
                            </p>
                            <button
                                onClick={openModal}
                                className="inline-flex items-center gap-3 bg-white text-mscg-dark-gray pl-6 pr-2 py-2.5 rounded-full font-medium text-sm transition-all duration-300 hover:bg-[#d68800] hover:text-white hover:scale-105 group"
                            >
                                Book Now
                                <div className="w-8 h-8 rounded-full bg-[#d68800] text-white flex items-center justify-center transition-all duration-300 group-hover:bg-white group-hover:text-[#d68800] group-hover:rotate-45">
                                    <ArrowUpRight className="w-4 h-4" />
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Outpatient Therapy Groups Section */}
            <TherapyGroupsSection />
        </div>
    );
};

// Therapy Groups Data
const therapyGroups = [
    {
        id: 'anger',
        title: 'Anger Management',
        description: `Anger can lead to problems with friends, family & at work. If unchecked, it can cause you to lose marriages, friends & other relationships, and even your job. We help you learn how to control your anger so it doesn't control you. Anger management classes are weekly & typically conducted in a group setting. Anger management can be attended as part of the Enhanced Outpatient Therapy program when taking Level I or Level II DUI Alcohol Classes.`,
        intake: '$75',
        session: '$30',
        note: 'The Intake Fee is waived if current client',
        image: '/images/Anger.jpeg',
    },
    {
        id: 'relapse',
        title: 'Relapse Prevention',
        description: `Relapse Prevention Therapy seeks to identify high-risk situations in which an individual is vulnerable to relapse & to use both cognitive & behavioral coping strategies to prevent future relapses in similar situations. The goal of treatment is to help people recognize the early stages of relapse, how to cope with triggers, and how to develop healthy coping skills to manage day to day life without the use of drugs & alcohol.\n\nClasses are weekly & typically conducted in a group setting. Individual relapse prevention is available. Relapse prevention can be attended voluntarily or as part of the Enhanced Outpatient Therapy program when taking Level I or Level II DUI Alcohol Classes.`,
        intake: '$75',
        session: '$30',
        note: 'The Intake Fee is waived if current client',
        image: '/images/Relapse.jpeg',
    },
    {
        id: 'iop',
        title: 'IOP Stepdown Therapy Group',
        description: `Weekly group support for those transitioning back into their daily lives recognizing the time needed to maintain employment and balance a busy lifestyle.`,
        intake: '$75',
        session: '$30',
        note: 'The Intake Fee is waived if current client',
        image: '/images/Stepdown.jpeg',
    },
    {
        id: 'dbt',
        title: 'Dialectical Behavior Therapy (DBT) Group',
        description: `A substance use-focused DBT approach encourages individuals to commit to abstinence and work to bolster their motivation to change through various activities and techniques.`,
        intake: '$75',
        session: '$30',
        note: 'The Intake Fee is waived if current client',
        image: '/images/DBT.jpeg',
    },
    {
        id: 'trem',
        title: 'Trauma Recovery & Empowerment',
        subtitle: 'Women Group Discussion',
        description: `A 24- to 29-session group intervention for women who survived trauma and have substance use and/or mental health conditions. This model draws on cognitive–behavioral, skills training, and psychoeducational techniques to address recovery and healing from sexual, physical, and emotional abuse.\n\nTREM consists of three major parts:\n• The first section on empowerment helps group members learn strategies for self-comfort, accurate self-monitoring, and ways to establish safe physical and emotional boundaries.\n• The second component focuses more directly on trauma experience and its consequences.\n• The third section shifts explicitly to skills building—communication style, decision-making, regulating overwhelming feelings, and establishing safer, more reciprocal relationships.`,
        intake: '$75',
        session: '$30',
        note: 'The Intake Fee is waived if current client',
        image: '/images/TREM.jpeg',
    },
    {
        id: 'multifamily',
        title: 'Multifamily Group',
        description: `This group is designed to teach the loved ones of those with mental health or substance use disorder how to use the key principles of Motivational Interviewing (MI) to foster a healthy relationship with their loved one. MI is based on the premise that the best way to influence another person's behavior is by respecting their autonomy and creating space for them to explore their own ambivalence and (hopefully) motivation for change.`,
        intake: '$75',
        session: '$30',
        note: 'The Intake Fee is waived if current client',
        image: '/images/Multifamily.jpeg',
    },
];

// Therapy Groups Section Component
const TherapyGroupsSection: React.FC = () => {
    const [activeTab, setActiveTab] = useState('anger');
    const activeGroup = therapyGroups.find(g => g.id === activeTab) || therapyGroups[0];
    const { openModal } = useBookingModal();

    return (
        <section className="pt-8 lg:pt-12 pb-24 lg:pb-32 bg-white">
            <div className="w-full px-6 sm:px-8 lg:px-12">

                {/* Tabs and Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                    {/* Vertical Tabs - Left Side */}
                    <div className="lg:col-span-4">
                        <div className="space-y-3">
                            {therapyGroups.map((group) => (
                                <button
                                    key={group.id}
                                    onClick={() => setActiveTab(group.id)}
                                    className={`w-full text-left px-6 py-4 rounded-2xl transition-all duration-300 group relative overflow-hidden ${activeTab === group.id
                                        ? 'bg-[#d68800] text-white shadow-lg shadow-[#d68800]/20'
                                        : 'bg-neutral-100 text-mscg-dark-gray hover:bg-neutral-200'
                                        }`}
                                >
                                    {/* Active indicator */}
                                    <div className={`absolute left-0 top-0 bottom-0 w-1 bg-white transition-transform duration-300 ${activeTab === group.id ? 'scale-y-100' : 'scale-y-0'
                                        }`} />

                                    <div className="flex items-center gap-3">
                                        <span className={`text-lg transition-transform duration-300 ${activeTab === group.id ? 'translate-x-1' : 'group-hover:translate-x-1'
                                            }`}>
                                            ›
                                        </span>
                                        <span className="font-medium text-sm md:text-base">{group.title}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Content Panel - Right Side */}
                    <div className="lg:col-span-8">
                        <div className="relative">
                            {/* Decorative line */}
                            <div className="hidden lg:block absolute -left-6 top-0 w-px h-16 bg-gradient-to-b from-[#d68800] to-transparent" />

                            {/* Content Card */}
                            <div className="bg-[#f8f7f5] rounded-3xl p-8 lg:p-10">
                                {/* Image floated right for text wrap */}
                                <div className="float-right ml-6 mb-4 w-full sm:w-64 lg:w-72">
                                    <div className="rounded-2xl overflow-hidden shadow-lg aspect-square">
                                        <img
                                            src={activeGroup.image}
                                            alt={activeGroup.title}
                                            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                                        />
                                    </div>
                                </div>

                                {/* Text Content - wraps around image */}
                                <div>
                                    {activeGroup.subtitle && (
                                        <p className="text-xs uppercase tracking-wider text-[#d68800] mb-2">
                                            {activeGroup.subtitle}
                                        </p>
                                    )}
                                    <h3 className="text-2xl lg:text-3xl font-serif text-mscg-dark-gray mb-4">
                                        {activeGroup.title}
                                    </h3>
                                    <div className="text-neutral-600 leading-relaxed text-sm lg:text-base whitespace-pre-line">
                                        {activeGroup.description}
                                    </div>
                                </div>

                                {/* Clear float */}
                                <div className="clear-both" />

                                {/* Pricing Card */}
                                <div className="mt-8 bg-mscg-dark-gray rounded-2xl p-5 flex flex-col md:flex-row items-center justify-between gap-4">
                                    <div className="flex flex-wrap items-center gap-4 lg:gap-6 text-white">
                                        <div className="flex items-center gap-2">
                                            <span className="w-6 h-6 rounded-full bg-[#d68800] flex items-center justify-center text-xs font-bold">$</span>
                                            <span className="text-sm">Intake: <strong>{activeGroup.intake}</strong></span>
                                        </div>
                                        <div className="w-px h-6 bg-white/20 hidden md:block" />
                                        <div className="flex items-center gap-2">
                                            <span className="w-6 h-6 rounded-full bg-[#d68800] flex items-center justify-center text-xs font-bold">$</span>
                                            <span className="text-sm">Each Session: <strong>{activeGroup.session}</strong></span>
                                        </div>
                                    </div>
                                    <button
                                        onClick={openModal}
                                        className="inline-flex items-center gap-2 bg-[#d68800] text-white px-6 py-2.5 rounded-full font-medium text-sm transition-all duration-300 hover:bg-[#891719] hover:scale-105"
                                    >
                                        Book Now
                                        <ArrowUpRight className="w-4 h-4" />
                                    </button>
                                </div>

                                {/* Waiver Note */}
                                <p className="mt-4 text-left text-xs text-neutral-500">
                                    ✦ {activeGroup.note}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
